import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Search, Download, ArrowUpRight } from "lucide-react";

// Mirror the visual tone palette used inside TesmModule (good / warn / muted / ink)
const BAND_PILL = (tone) => {
  switch (tone) {
    case "good": return "bg-good-soft text-good border-good/30";
    case "warn": return "bg-warn-soft text-warn border-warn/30";
    case "muted": return "bg-secondary text-ink-muted border-ink-line";
    case "ink": return "bg-secondary text-ink border-ink-line";
    default: return "bg-secondary text-ink-muted border-ink-line";
  }
};

const BANDS = [
  { name: "Strong",   tone: "good" },
  { name: "Moderate", tone: "warn" },
  { name: "Weak",     tone: "muted" },
  { name: "Poor",     tone: "ink" },
];

export default function TesmSubmissions() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (band) params.set("band", band);
      const { data } = await api.get(`/tesm/submissions?${params.toString()}`);
      setItems(data.items);
      setStats(data.stats);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [band]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const downloadAll = async () => {
    try {
      const resp = await api.get(`/tesm/submissions/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tesm_submissions.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="tesm-submissions-page">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-ink-line pb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand text-white grid place-items-center rounded-sm">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Early Screening</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1">TESM Submissions</h1>
            <p className="text-sm text-ink-muted mt-1.5">Per-tender Tender Early Screening Matrix responses with full scoring detail.</p>
          </div>
        </div>
        <button
          onClick={downloadAll}
          data-testid="tesm-export-all-btn"
          className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2.5 text-sm font-medium rounded-sm transition-colors"
        >
          <Download className="w-4 h-4" /> Export all CSV
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="card-flat p-4">
            <div className="stat-label">Total</div>
            <div className="font-num text-3xl font-bold mt-2">{stats.total}</div>
          </div>
          <div className="card-flat p-4">
            <div className="stat-label">Avg score</div>
            <div className="font-num text-3xl font-bold mt-2 text-brand">{stats.avg_score || 0}</div>
          </div>
          {BANDS.map((b) => (
            <div key={b.name} className="card-flat p-4" data-testid={`tesm-band-count-${b.name.toLowerCase()}`}>
              <div className="stat-label">{b.name}</div>
              <div className="font-num text-3xl font-bold mt-2">{stats.band_counts?.[b.name] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="tesm-search"
            placeholder="Search by client, title, location, submitter…"
            className="w-full pl-9 pr-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
          />
        </div>
        <div className="flex gap-0 border border-ink-line rounded-sm bg-white overflow-hidden">
          <button onClick={() => setBand("")} data-testid="tesm-band-filter-all" className={`px-3 py-2 text-[12px] uppercase tracking-wider border-r border-ink-line ${band === "" ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}>All</button>
          {BANDS.map((b, i) => (
            <button
              key={b.name}
              onClick={() => setBand(b.name)}
              data-testid={`tesm-band-filter-${b.name.toLowerCase()}`}
              className={`px-3 py-2 text-[12px] uppercase tracking-wider ${i < BANDS.length - 1 ? "border-r border-ink-line" : ""} ${band === b.name ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3 font-medium">Submitted</th>
              <th className="px-5 py-3 font-medium">By</th>
              <th className="px-5 py-3 font-medium">Client</th>
              <th className="px-5 py-3 font-medium">Tender</th>
              <th className="px-5 py-3 font-medium">Location</th>
              <th className="px-5 py-3 font-medium text-right">Score</th>
              <th className="px-5 py-3 font-medium">Band</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-5 py-10 text-center text-ink-muted uppercase tracking-wider text-xs">Loading…</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-ink-muted">No TESM submissions yet.</td></tr>
            )}
            {items.map((s) => {
              const t = s.tender || {};
              return (
                <tr key={s.tender_id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                  <td className="px-5 py-3 text-ink-muted font-num text-xs">
                    {s.submitted_at ? new Date(s.submitted_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-5 py-3">{s.submitted_by_name || "—"}</td>
                  <td className="px-5 py-3 font-medium">{t.client || "—"}</td>
                  <td className="px-5 py-3 max-w-xs truncate" title={t.title}>{t.title || "—"}</td>
                  <td className="px-5 py-3 text-ink-muted">{t.location || "—"}</td>
                  <td className="px-5 py-3 text-right font-num font-bold text-base">{s.score}</td>
                  <td className="px-5 py-3"><span className={`chip ${BAND_PILL(s.band_tone)}`} data-testid={`tesm-row-band-${s.tender_id}`}>{s.band || "—"}</span></td>
                  <td className="px-5 py-3 text-right">
                    <Link
                      to={`/admin/tesm/${s.tender_id}`}
                      data-testid={`tesm-view-submission-${s.tender_id}`}
                      className="inline-flex items-center gap-1 text-brand hover:text-brand-hover text-[13px] font-medium"
                    >
                      View <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
